import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Package } from "lucide-react";
import { apiClient } from "../../services/apiClientService";
import TrackOrderTimeline from "./TrackOrderTimeline";

const formatMoney = (amountInPaise) => {
  const n = Number(amountInPaise);
  if (Number.isNaN(n)) return "—";
  return `₹${(n / 100).toFixed(2)}`;
};

const OrderHistoryPage = () => {
  const [orders, setOrders] = useState([]);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [reviewProduct, setReviewProduct] = useState(null);
  const [reviewOrderId, setReviewOrderId] = useState(null);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const res = await apiClient("GET", "/api/orders/my");
        setOrders(Array.isArray(res?.orders) ? res.orders : []);
      } catch (err) {
        console.error("Failed to load orders:", err.message);
        setOrders([]);
      }
    };

    loadOrders();
  }, []);

  const toggleOrder = (id) => {
    setOpenOrderId((prev) => (prev === id ? null : id));
  };

  const orderLabel = (order) =>
    order.razorpayOrderId || order.orderId || order._id?.slice(-8) || "Order";

  const fulfillmentHint = (order) => {
    if (!order?.itemReadyAt) return null;
    const m = String(order.shippingInfo?.receivingMethod || "delivery").toLowerCase();
    return m === "pickup" ? "Ready for pickup" : "Ready for delivery";
  };

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-6 sm:mb-8">
        <p className="text-xs font-bold uppercase tracking-widest text-orange-600">Orders</p>
        <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-stone-900 sm:text-3xl">My orders</h2>
        <p className="mt-2 text-sm text-stone-600">Tap a row to see items and tracking.</p>
      </header>

      {orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/60 px-6 py-12 text-center">
          <Package className="mx-auto h-10 w-10 text-stone-400" strokeWidth={1.75} />
          <p className="mt-3 text-sm font-medium text-stone-700">No orders yet</p>
          <p className="mt-1 text-sm text-stone-500">When you buy something, it will show up here.</p>
          <Link
            to="/shop"
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-2.5 text-sm font-bold text-white shadow-md shadow-orange-300/35 transition hover:from-orange-600 hover:to-amber-600"
          >
            Browse shop
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {orders.map((order) => {
            const id = order._id;
            const open = openOrderId === id;
            const placed = new Date(order.createdAt).toLocaleDateString(undefined, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });
            const itemCount = order.products?.length ?? 0;

            return (
              <li
                key={id}
                className="overflow-hidden rounded-2xl border border-stone-200/90 bg-white shadow-sm ring-1 ring-black/[0.02]"
              >
                <button
                  type="button"
                  onClick={() => toggleOrder(id)}
                  className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition hover:bg-stone-50/80 sm:items-center sm:gap-4 sm:px-5 sm:py-4"
                  aria-expanded={open}
                  aria-controls={`order-details-${id}`}
                >
                  <span
                    className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:mt-0 ${
                      open ? "bg-orange-500 text-white shadow-md shadow-orange-300/40" : "bg-stone-100 text-stone-500"
                    }`}
                  >
                    <Package className="h-4 w-4" strokeWidth={2.25} />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="truncate text-sm font-bold text-stone-900 sm:text-base">
                        {orderLabel(order)}
                      </span>
                      {itemCount > 0 ? (
                        <span className="text-xs font-medium text-stone-500">
                          · {itemCount} {itemCount === 1 ? "item" : "items"}
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block text-xs text-stone-500">Placed on {placed}</span>
                  </span>

                  <span className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-3">
                    {order.sellerDecision === "pending" ? (
                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-900">
                        Awaiting seller
                      </span>
                    ) : order.sellerDecision === "rejected" ? (
                      <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-red-800">
                        Declined
                      </span>
                    ) : null}
                    {order.itemReadyAt ? (
                      <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-orange-900">
                        {fulfillmentHint(order)}
                      </span>
                    ) : null}
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                        String(order.status).toLowerCase() === "delivered"
                          ? "bg-emerald-100 text-emerald-800"
                          : String(order.status).toLowerCase() === "packed"
                            ? "bg-sky-100 text-sky-900"
                            : "bg-stone-100 text-stone-700"
                      }`}
                    >
                      {order.status || "placed"}
                    </span>
                    <span className="text-sm font-extrabold tabular-nums text-stone-900">
                      {formatMoney(order.amount)}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-stone-400 transition-transform ${
                        open ? "rotate-180" : ""
                      }`}
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </span>
                </button>

                {open ? (
                  <div
                    id={`order-details-${id}`}
                    className="border-t border-stone-100 bg-stone-50/50 px-4 py-4 sm:px-5"
                  >
                    {order.products?.length > 0 ? (
                      <div className="mb-4">
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-stone-500">
                          Items
                        </p>
                        <ul className="divide-y divide-stone-200/80 rounded-xl border border-stone-200 bg-white">
                          {order.products.map((item, idx) => (
                            <li
                              key={`${id}-${idx}`}
                              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm sm:px-4"
                            >
                              <span className="min-w-0 font-medium text-stone-900">{item.name}</span>
                              <span className="flex shrink-0 items-center gap-3 text-xs text-stone-600 sm:text-sm">
                                <span>Qty {item.quantity}</span>
                                <span className="font-semibold tabular-nums text-stone-800">
                                  ₹{Number(item.price).toFixed(2)}
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    <div className="mb-4 rounded-xl border border-stone-200 bg-white p-3 sm:p-4">
                      <TrackOrderTimeline order={order} />
                    </div>

                    <Link
                      to={`/order/${id}`}
                      className="inline-flex items-center gap-1 text-sm font-bold text-orange-700 transition hover:text-orange-900"
                    >
                      Full order details
                      <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
                    </Link>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default OrderHistoryPage;
